import { FC, useState, useMemo, useContext } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, pdf, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { Button, App, Dropdown, Tooltip } from "antd";
import { PrinterOutlined } from '@ant-design/icons';
import { DictContext } from "@/layout/context/DictContext";
import { getFileUrl } from "@/apis/file";
import zioncom from 'images/system/zioncom.png';
import JsBarcode from "jsbarcode";
import convertTo2DArray, { generateDiy2DArray, sortOrderItems } from "@/utils/convert2double";
import { OrderV2, OrderItemV2, OrderItemLocation } from "@/types/inventoryV2";
import { getLocationIdx } from "@/utils/wLocation";
import { opTypes2 } from "@/pages/Warehouse/utils";
import useDataList from "@/hooks/useDataList";
import { CommonListParams } from "@/types/common";
import { inventoryOrders } from "@/apis/requirement";
import truncateString from "@/utils/stringOver";
import { Requirement, RequirementItem, RequirementItemDelEnum } from "@/types/requirement";
import { numFormmater } from "@/pages/Requirement/util";
import { Product } from "@/types/product";
import { upload } from "@/utils/file/upload";
import { docAdd } from "@/apis/doc";
import { DocTypeEnum } from "@/types/doc";
import { orderPrint } from "@/apis/inventoryv2";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import { computedPrint, initPrintSetting } from "@/layout/HomeLayout/utils";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
  },

  // 标题部分
  titleSection: {
    position: 'relative' as any,
    marginBottom: 8
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center' as any
  },
  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: -4,
    flexDirection: 'column',
    alignItems: 'center'
  },
  barcodeText: {
    fontSize: 6,
    marginTop: -8
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black",
    // marginTop: 0
  },
  tableRow: {
    flexDirection: "row"
  },

  // 单元格样式
  tableCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as any,
    letterSpacing: 0,
    color: '#000'
  },

  // 条码单元格
  barcodeCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 1,
    fontSize: 5,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#000'
  },

  // 表头单元格
  tableHeaderCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    letterSpacing: 0
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center' as any
  },
  bottomInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    // paddingHorizontal: 24
  },
  bottomItem: {
    flex: 1,
    textAlign: "left"
  },
  bottomText: {
    fontSize: 8
  },
  printInfo: {
    position: "absolute",
    bottom: 24,
    right: 30,
    fontSize: 6,
    color: "#000",
    textAlign: "right",
  },
  // 页脚
  footer: {
    position: "absolute",
    bottom: 24,
    left: 30,
    right: 30,
    textAlign: 'center' as any,
    fontSize: 6,
    color: '#000',
  },
  vnFont: {
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
  },
  printCount: { fontSize: 8, position: "absolute", top: 12, right: 0, textAlign: "right" },
  printCountNormal: { fontSize: 8, position: "absolute", bottom: -2, right: 0, textAlign: "right" }
});

// 扩展类型以包含条码URL
interface CodeUrlItem {
  id: string;
  sourceId: string;
  count: number;
  product: Product | null;
  locations: Array<OrderItemLocation>;
  bomAmount: string;
  codeUrl?: string;
  lotCodeUrl?: string;  // 批次号条码
  desc: string;
}

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}
export interface OutQtyObj {
  preOutQty: number;
  outQty: number;
  useAmount: number;
  bomAmount: number;
}

interface PreOutOrderPrintProps {
  order: OrderV2;
  onStart?: () => void;
  onEnd?: () => void;
  disabled?: boolean;
  buttonText?: string;
  reqId?: string;
  requirement?: Requirement;
  preOutQtyMap?: Record<string, OutQtyObj>;
  onPrinted?: () => void;
}

const PreOutOrderPrint: FC<PreOutOrderPrintProps> = ({ order, onStart, onEnd, disabled = false, buttonText, reqId = '', requirement, preOutQtyMap, onPrinted }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);
  const { printSetting } = useContext(DictContext);

  const { width: printWitdh, height: printHeight } = computedPrint();
  const { width: settingWitdh, height: settingHeight } = initPrintSetting();

  const [, , orderLoading, { fetchList }] = useDataList<OrderV2, [string, CommonListParams]>({
    fetchApi: inventoryOrders
  });

  const curUser = useSelector((state: RootState) => state.userReducer.curUser)

  const printCountStr = useMemo(() => {
    return (order?.printCount || 0) + 1 + '';
  }, [order]);

  const printerStr = useMemo(() => {
    let time = dateFormat(dayjs(), true);
    if (!!curUser.name1 && !!curUser.name2) return `${curUser.name1}(${curUser.name2}) ${time}`
    else return `${curUser.name1 || curUser.name2 || curUser.mobile} ${time}`
  }, [curUser]);

  // 获取公司logo
  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name };

  }, [printSetting]);

  const rowData: CodeUrlItem[] = useMemo(() => {
    // if (!!requirement && !!order && Array.isArray(requirement?.items) && Array.isArray(order.items)) {
    if (!!requirement && !!order && Array.isArray(requirement?.items) && Array.isArray(order.items)) {
      let resData: CodeUrlItem[] = [];
      order.items.forEach(orderItem => {
        let reqItem = requirement.items.find(ri => orderItem.sourceId === ri.id);
        resData.push({
          id: orderItem.id,
          sourceId: orderItem.sourceId || '',
          product: orderItem.product ? { ...orderItem.product } : null,
          count: orderItem?.count || 0,
          locations: orderItem?.locations ? [...orderItem.locations] : [],
          bomAmount: reqItem?.bomAmount || '',
          desc: orderItem?.desc ?? ''
        })
      })
      resData.sort(sortOrderItems);
      return [...resData]
    } else return []
  }, [requirement, order])


  // 生成条码
  const handleCode = (code: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!code || code === '-') {
        resolve('');
        return;
      }
      const canvasDom = document.createElement("canvas");
      JsBarcode(canvasDom, code, {
        format: "CODE128",
        width: 1.5,
        height: 20,
        displayValue: false
      });
      canvasDom.toBlob((blob) => {
        canvasDom.remove();
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject("条码生成失败");
        }
      }, 'image/png');
    });
  };

  // 获取opType2文本
  const getOpType2Text = (data: OrderV2) => {
    if (!data.opType2) return '';
    return opTypes2.find(op => op.value === data.opType2)?.label || '';
  };

  // 生成PDF文档
  const MyDocument = (codeItems: CodeUrlItem[], orderBarcode: string, orderNumber: string, opType2Text: string) => {
    let seq = 0;
    let cusOrderQty: number = 0;
    requirement?.saleOrder?.items?.forEach((i => {
      cusOrderQty = cusOrderQty + (Number(i.quantity) || 0)
      cusOrderQty = cusOrderQty + (Number(i.bquantity) || 0)
    }))

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={[styles.page, { padding: 28 }]}>
          {/* 标题栏 - 只在第一页显示 */}
          <View style={styles.titleSection}>
            {/* <Text style={styles.title}>{name ?? '吉翁科技（深圳）有限公司'} --- ({t('warehouse.pickingOutbound', { lng: "zh" })})</Text> */}
            <Text style={{ ...styles.title, fontSize: 10 }}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
            <Text style={{ ...styles.title, fontSize: 8 }}>
              {t('warehouse.pickingOutbound_m', { lng: "zh" })}/{t('warehouse.pickingOutbound_m', { lng: "vn" })}
            </Text>
            <Text style={{ fontSize: 8, letterSpacing: -1, marginTop: 10, marginBottom: -2 }}>
              {getBlankWord(`${t('sale.reRemark', { lng: "zh" })}(${t('sale.reRemark', { lng: "vn" })}): ${order.desc || '-'}`, true)}
            </Text>
            {orderBarcode && (
              <View style={styles.titleBarcode}>
                <Image src={orderBarcode} style={{ width: 110, height: 32 }} />
                <Text style={styles.barcodeText}>{orderNumber || ''}</Text>
              </View>
            )}
            <Text style={styles.printCountNormal}>{`${t('order.printCount', { lng: "zh" })}(${t('order.printCount', { lng: "vn" })}): ${printCountStr}`}</Text>
          </View>

          {/* 表格 */}
          <View style={{ ...styles.table, borderLeftWidth: 0 }}>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.outNo', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.outNo', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>

              <View style={[styles.tableHeaderCol, { width: '16%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(order?.number ?? '')}</Text>
              </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.innerNo', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.innerNo', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(requirement?.saleOrder?.innerNumber ?? '')}</Text>
              </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('product.companyModel', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('product.companyModel', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(requirement?.saleOrder?.mfgr ?? '')}</Text>
              </View>

            </View>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.outData', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.outData', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '16%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.outWare', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.outWare', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.llorg', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.llorg', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}> </View>
            </View>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '34%' }]}>
                <Text style={styles.fontStyle}>
                  {t('requirement.cusOrderQty', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('requirement.cusOrderQty', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '33%' }]}>
                {requirement?.saleOrder?.items?.length && requirement.saleOrder.items.length > 1 ? <Text style={styles.fontStyle}>{numFormmater(cusOrderQty)}</Text>
                  : <Text style={styles.fontStyle}>
                    {t('requirement.cusOrderQtyTip', { lng: "zh" })}
                    <Text style={styles.vnFont}>{getBlankWord(`(${t('requirement.cusOrderQtyTip', { lng: "vn" })}):`)}</Text>
                  </Text>
                }
              </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.llper', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.llper', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{opType2Text}</Text>
              </View>

            </View>
            <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
              return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
            }}></View>
            {/* 表头 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]} fixed>
              {/* 序号 */}
              <View style={[styles.tableHeaderCol, { width: '4%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.serialMumber', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('order.serialMumber', { lng: "vn" }))}</Text>
              </View>
              {/* 料号列 */}
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printCode', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }))}</Text>
              </View>
              {/* 品名列 */}
              <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printName', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('purchase.printName', { lng: "vn" }))}</Text>
              </View>
              {/* 型号列 */}
              <View style={[styles.tableHeaderCol, { width: '30%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.spec', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('purchase.spec', { lng: "vn" }))}</Text>
              </View>
              {/* 库位索引列 */}
              <View style={[styles.tableHeaderCol, { width: '9%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.locationIndex', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('warehouse.locationIndex', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.orderCount', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('warehouse.orderCount', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '9%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.useAmtReq', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('warehouse.useAmtReq', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.outCount', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('requirement.outCount', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.itemDesc', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('requirement.itemDesc', { lng: "vn" }))}</Text>
              </View>

            </View>

            {/* 表体 */}
            {codeItems.map((item, index) => {
              return (
                // 序号
                <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={index} wrap={false}>
                  <View style={[styles.tableCol, { width: '4%' }]}>
                    <Text style={styles.fontStyle}>{++seq}</Text>
                  </View>
                  {/* 料号列 */}
                  <View style={[styles.tableCol, { width: '10%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(item.product?.code || '', true)}
                    </Text>
                  </View>
                  {/* 品名列 */}
                  <View style={[styles.tableCol, { width: '12%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(`${item.product?.name || ''}${item.bomAmount ? '(' + item.bomAmount + ')' : ''}`, true)}
                    </Text>
                  </View>
                  {/* 型号列 */}
                  <View style={[styles.tableCol, { width: '30%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord((truncateString(item.product?.model || '', 56)), true)}
                    </Text>
                  </View>
                  {/* 库位索引列 */}
                  <View style={[styles.tableCol, { width: '9%' }]}>
                    {item.locations && item.locations.length > 0 ? (
                      item.locations.map((loc, locIndex) => (
                        <Text key={locIndex} style={[{ width: "100%", textAlign: "left", fontSize: 6, letterSpacing: -0.5 }]}>
                          {getBlankWord(`${getLocationIdx(loc.location) || '-'}(${loc.count})`, true)}
                        </Text>
                      ))
                    ) : (
                      <Text style={styles.fontStyle}>{getBlankWord('-')}</Text>
                    )}
                  </View>
                  <View style={[styles.tableCol, { width: '10%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(numFormmater(item.count || 0), true)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '9%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(numFormmater(preOutQtyMap?.[item.sourceId]?.useAmount || 0), true)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '8%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(numFormmater(preOutQtyMap?.[item.sourceId]?.outQty || 0), true)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '8%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(item.desc, true)}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>

          {/* Logo图片 - 只在第一页显示 */}
          <Image src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,
            height: 30,
            objectFit: "scale-down"
          }} />
          <View style={styles.bottomInfo}>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>
                {t('warehouse.outper', { lng: "zh" })}
                <Text style={styles.vnFont}>({t('warehouse.outper', { lng: "vn" })}):</Text>
              </Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>
                {t('warehouse.wareper', { lng: "zh" })}
                <Text style={styles.vnFont}>({t('warehouse.wareper', { lng: "vn" })}):</Text>
              </Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>
                {t('warehouse.outCheckDate', { lng: "zh" })}
                <Text style={styles.vnFont}>({t('warehouse.outCheckDate', { lng: "vn" })}):</Text>
              </Text>
            </View>
          </View>

          {/* 页脚 */}
          <Text style={styles.footer} render={({ pageNumber, totalPages }) =>
            // `${t('purchase.pageIndex', { index: pageNumber, total: totalPages })}`
            `P ${pageNumber} / ${totalPages}`
          } fixed />
          <Text fixed style={styles.printInfo} >{printerStr}</Text>
        </Page>
      </Document>
    );
  };
  const MyVerticalDocument = (codeItems: CodeUrlItem[], orderBarcode: string, orderNumber: string, opType2Text: string) => {
    let cusOrderQty: number = 0;
    requirement?.saleOrder?.items?.forEach((i => {
      cusOrderQty = cusOrderQty + (Number(i.quantity) || 0)
      cusOrderQty = cusOrderQty + (Number(i.bquantity) || 0)
    }))
    return (
      <Document>
        <Page size={{ width: printWitdh, height: printHeight }} orientation="portrait" style={[styles.page, { paddingHorizontal: 4 }]} wrap>
          <View fixed render={({ pageNumber }) => {
            return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000" }} />
          }}>
          </View>
          {/* 标题栏 - 只在第一页显示 */}
          <View style={styles.titleSection}>
            {/* <Text style={styles.title}>{name ?? '吉翁科技（深圳）有限公司'} --- ({t('warehouse.pickingOutbound', { lng: "zh" })})</Text> */}
            <Text style={{ ...styles.title, fontSize: 10 }}>{name ?? '吉翁科技（深圳）有限公司'}</Text>
            <Text style={{ ...styles.title, fontSize: 8 }}>
              {t('warehouse.pickingOutbound_m', { lng: "zh" })}/{t('warehouse.pickingOutbound_m', { lng: "vn" })}
              {'    '}
              {orderNumber ?? ''}
            </Text>
            <Text style={{ fontSize: 8, letterSpacing: -1, marginTop: 10, marginBottom: -2 }}>
              {getBlankWord(`${t('sale.reRemark', { lng: "zh" })}(${t('sale.reRemark', { lng: "vn" })}): ${order.desc || '-'}`, true)}
            </Text>
            <Text style={styles.printCount}>{`${t('order.printCount', { lng: "zh" })}(${t('order.printCount', { lng: "vn" })}): ${printCountStr}`}</Text>
          </View>

          {/* 表格 */}
          <View style={{ ...styles.table, flexShrink: 0, borderLeftWidth: 0 }}>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.outNo', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.outNo', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>

              <View style={[styles.tableHeaderCol, { width: '16%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(order?.number ?? '')}</Text>
              </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.innerNo', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.innerNo', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(requirement?.saleOrder?.innerNumber ?? '')}</Text>
              </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('product.companyModel', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('product.companyModel', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(requirement?.saleOrder?.mfgr ?? '')}</Text>
              </View>

            </View>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.outData', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.outData', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '16%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.outWare', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.outWare', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}> </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.llorg', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.llorg', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}> </View>
            </View>
            {/* 头部信息 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              <View style={[styles.tableHeaderCol, { width: '34%' }]}>
                <Text style={styles.fontStyle}>
                  {t('requirement.cusOrderQty', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('requirement.cusOrderQty', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '33%' }]}>
                {requirement?.saleOrder?.items?.length && requirement.saleOrder.items.length > 1 ? <Text style={styles.fontStyle}>{numFormmater(cusOrderQty)}</Text>
                  : <Text style={styles.fontStyle}>
                    {t('requirement.cusOrderQtyTip', { lng: "zh" })}
                    <Text style={styles.vnFont}>{getBlankWord(`(${t('requirement.cusOrderQtyTip', { lng: "vn" })}):`)}</Text>
                  </Text>
                }
              </View>

              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>
                  {t('warehouse.llper', { lng: "zh" })}
                  <Text style={styles.vnFont}>{getBlankWord(`(${t('warehouse.llper', { lng: "vn" })}):`)}</Text>
                </Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '15%' }]}>
                <Text style={styles.fontStyle}>{opType2Text}</Text>
              </View>

            </View>

            {/* 表头 */}
            <View style={[styles.tableRow, { borderLeftWidth: 1 }]}>
              {/* 序号 */}
              <View style={[styles.tableHeaderCol, { width: '6%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('order.serialMumber', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('order.serialMumber', { lng: "vn" }))}</Text>
              </View>
              {/* 料号列 */}
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printCode', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }))}</Text>
              </View>
              {/* 品名列 */}
              <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printName', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('purchase.printName', { lng: "vn" }))}</Text>
              </View>
              {/* 型号列 */}
              <View style={[styles.tableHeaderCol, { width: '28%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.spec', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('purchase.spec', { lng: "vn" }))}</Text>
              </View>
              {/* 库位索引列 */}
              <View style={[styles.tableHeaderCol, { width: '9%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.locationIndex', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('warehouse.locationIndex', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '10%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.orderCount', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('warehouse.orderCount', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '9%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('warehouse.useAmtReq', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('warehouse.useAmtReq', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.outCount', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('requirement.outCount', { lng: "vn" }))}</Text>
              </View>
              {/* 数量列 */}
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.itemDesc', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.vnFont }}>{getBlankWord(t('requirement.itemDesc', { lng: "vn" }))}</Text>
              </View>

            </View>

            {/* 表体 */}
            {codeItems.map((item: CodeUrlItem, index: number) => {
              return (
                // 序号
                <View style={[styles.tableRow, { borderLeftWidth: 1 }]} key={index} wrap={false}>
                  <View style={[styles.tableCol, { width: '6%' }]}>
                    <Text style={styles.fontStyle}>{index + 1}</Text>
                  </View>
                  {/* 料号列 */}
                  <View style={[styles.tableCol, { width: '10%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(item.product?.code || '', true)}
                    </Text>
                  </View>
                  {/* 品名列 */}
                  <View style={[styles.tableCol, { width: '12%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(`${item.product?.name || ''}${item.bomAmount ? '(' + item.bomAmount + ')' : ''}`, true)}
                    </Text>
                  </View>
                  {/* 型号列 */}
                  <View style={[styles.tableCol, { width: '28%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord((item.product?.model || ''), true)}
                    </Text>
                  </View>
                  {/* 库位索引列 */}
                  <View style={[styles.tableCol, { width: '9%' }]}>
                    {item.locations && item.locations.length > 0 ? (
                      item.locations.map((loc, locIndex) => (
                        <Text key={locIndex} style={[{ width: "100%", textAlign: "left", fontSize: 6, letterSpacing: -0.5 }]}>
                          {getBlankWord(`${getLocationIdx(loc.location) || '-'}(${loc.count})`, true)}
                        </Text>
                      ))
                    ) : (
                      <Text style={styles.fontStyle}>{getBlankWord('-')}</Text>
                    )}
                  </View>
                  <View style={[styles.tableCol, { width: '10%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(numFormmater(item.count || 0), true)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '9%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(numFormmater(preOutQtyMap?.[item.sourceId]?.useAmount || 0), true)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '8%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(numFormmater(preOutQtyMap?.[item.sourceId]?.outQty || 0), true)}
                    </Text>
                  </View>
                  <View style={[styles.tableCol, { width: '8%' }]}>
                    <Text style={[styles.fontStyle, { flexWrap: 'wrap', textAlign: 'left', letterSpacing: -1 }]}>
                      {getBlankWord(item.desc, true)}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
          <View style={styles.bottomInfo}>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>
                {t('warehouse.outper', { lng: "zh" })}
                <Text style={styles.vnFont}>({t('warehouse.outper', { lng: "vn" })}):</Text>
              </Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>
                {t('warehouse.wareper', { lng: "zh" })}
                <Text style={styles.vnFont}>({t('warehouse.wareper', { lng: "vn" })}):</Text>
              </Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>
                {t('warehouse.outCheckDate', { lng: "zh" })}
                <Text style={styles.vnFont}>({t('warehouse.outCheckDate', { lng: "vn" })}):</Text>
              </Text>
            </View>
          </View>
          <View style={{ marginTop: 30, flexDirection: "row", justifyContent: "flex-end" }}>
            <Text style={{ bottom: 16, right: 4, fontSize: 8 }} >{printerStr}</Text>
          </View>
        </Page>
      </Document>
    );
  };

  // 处理PDF打印
  const handlePDF = async (flag: boolean) => {
    let getOrder = { ...order };
    if ((!getOrder?.items || getOrder.items.length === 0) && reqId === '') {
      return notification.error({
        placement: 'top',
        message: t('purchase.printErr'),
        description: t('common.noOutOrder')
      });
    }
    if (reqId) {
      const params: CommonListParams = {
        pagination: { offset: 0, take: 1 },
      }
      let orderBody = await fetchList(reqId, params)
      if (orderBody?.body && orderBody.body.length > 0) {
        getOrder = orderBody.body[0];
      } else {
        return notification.error({
          placement: 'top',
          message: t('purchase.printErr'),
          description: t('common.noOutOrder')
        });
      }
    }

    setLoading(true);
    if (onStart) onStart();

    try {
      // 生成订单号条码
      let orderBarcode = '';
      try {
        if (getOrder.number) {
          orderBarcode = await handleCode(getOrder.number);
        }
      } catch (error) {
        console.error('订单号条码生成失败:', error);
      }

      // 为每个产品生成条码
      // let codeItems: CodeUrlItem[] = [];
      // for (let i = 0; i < getOrder.items.length; i++) {
      //   const item = getOrder.items[i];
      //   let codeUrl = '';
      //   let lotCodeUrl = '';
      //   try {
      //     if (item.product?.code) {
      //       codeUrl = await handleCode(item.lot);
      //     }
      //   } catch (error) {
      //     console.error('条码生成失败:', item.product?.code, error);
      //   }
      //   // 生成批次号条码
      //   try {
      //     if (item.lot) {
      //       lotCodeUrl = await handleCode(item.lot);
      //     }
      //   } catch (error) {
      //     console.error('批次号条码生成失败:', item.lot, error);
      //   }
      //   codeItems.push({
      //     ...item,
      //     codeUrl,
      //     lotCodeUrl
      //   });
      // }

      // 生成PDF文档
      const opType2Text = getOpType2Text(getOrder);
      const pageElement = flag ? MyVerticalDocument(rowData, orderBarcode, getOrder.number, opType2Text) : MyDocument(rowData, orderBarcode, getOrder.number, opType2Text);
      if (!pageElement) {
        notification.error({ placement: 'top', message: t('purchase.printErr') });
        setLoading(false);
        return;
      }

      // 将文档转换为Blob
      const blob = await pdf(pageElement).toBlob();
      let name = `${order.number}(${t('warehouse.pickingOutbound_m')})-${printerStr}(${flag ? t('warehouse.verticalPrint') : t('warehouse.normalPrint')}).pdf`;
      let docId = order.docId;
      if (docId) {
        let file = await upload({ name, size: blob.size, file: blob });
        if (file) {
          let doc = await docAdd({ pId: docId, name, suffix: file.suffix, nodeType: DocTypeEnum.LEAF, fileId: file.id });
          await orderPrint(order.id, doc.id);
          // 打印完成后，更新打印次数
          onPrinted?.();
        }
      }
      const pdfUrl = URL.createObjectURL(blob);

      // 创建隐藏的iframe用于打印
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将iframe添加到body中
      document.body.appendChild(iframe);

      iframe.onload = () => {
        console.log('PDF 加载完成，等待额外时间');
        try {
          console.log('最终调用 print()');
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
          notification.error({ placement: 'top', message: '打印失败' });
        } finally {
          setLoading(false);
          if (onEnd) onEnd();
        }
      };

    } catch (error) {
      console.error('生成PDF失败:', error);
      notification.error({ placement: 'top', message: '生成PDF失败' });
      setLoading(false);
      if (onEnd) onEnd();
    }
  };

  return (
    <Dropdown trigger={["click"]}
      menu={{
        items: [
          {
            key: 'NORM',
            label: t('warehouse.normalPrint'),
            onClick: () => handlePDF(false),

          },
          {
            key: 'WARE',
            label: <Tooltip title={t('layout.printTip', { width: settingWitdh, height: settingHeight })}>{t('warehouse.verticalPrint')}</Tooltip>,
            onClick: () => handlePDF(true),
          },
        ],
      }}
    >
      <Button type="primary" icon={<PrinterOutlined />} loading={loading} disabled={disabled}>{buttonText || t('common.printInfo')}</Button>
    </Dropdown>
  );

};
export default PreOutOrderPrint;
